import _ from "lodash"
import { useCallback, useEffect, useId, useMemo, useRef } from "react"
import { PlayerEvent, playerStore, usePlayList } from "src/store/PlayerStore"

import { secondsToTime, s } from "../../utilities/common"
import { dimensions } from "../../utilities/constants"

import { videoUIWidth } from "./UI/VideoUI"
import { usePlayer } from "./use-player"

// for performance reasons, we have to manipulate the views directly
const timeToProgress = (currentTime: number, duration: number) => {
  return (currentTime / duration) * 100
}

export const useProgress = (
  props: { addListeners: boolean } = { addListeners: false },
) => {
  const setEventListener = playerStore((state) => state.setEventListener)
  const removeEventListener = playerStore((state) => state.removeEventListener)
  const videoRef = playerStore((state) => state.videoRef)
  const cuePoints = playerStore((state) => state.cuePoints)
  const setTrackId = playerStore((state) => state.setTrackId)
  const currentTimeRef = playerStore((state) => state.currentTimeRef)
  const { seek: seekStore, duration, paused, pause, play } = usePlayer()

  const cuePointTimespan = useMemo(
    () => (s(16) / videoUIWidth) * duration, // 16: cuepoint width
    [duration],
  )

  const content = playerStore((state) => state.content)
  const playList = usePlayList(content)
  const trackId = playerStore((state) => state.trackId)
  const shuffledPlaylist = playerStore((state) => state.shuffledPlaylist)

  const shuffle = playerStore((state) => state.shuffle)
  const repeat = playerStore((state) => state.repeat)

  const nextUp = useCallback(
    (direction: "left" | "right") => {
      const allTracks =
        shuffle &&
        (content?.__typename === "Album" ||
          content?.__typename === "EditorialPlaylist")
          ? shuffledPlaylist
          : playList?.flatMap((set) => set.items)
      if (allTracks) {
        const index = allTracks.findIndex((track) => track.id === trackId)
        // pressing next ignores the repeat: single setting
        /*if (repeat === "single") {
          setTrackId(trackId)
          return true
        }*/
        if (direction === "left" && allTracks[index - 1]) {
          setTrackId(allTracks[index - 1].id)
          return true
        }
        if (direction === "right") {
          if (
            !allTracks[index + 1] &&
            (repeat === "none" ||
              (content?.__typename !== "Album" &&
                content?.__typename !== "EditorialPlaylist"))
          )
            return false
          setTrackId(
            !!allTracks[index + 1] ? allTracks[index + 1].id : allTracks[0].id,
          )
          return true
        }
        return false
      }
      return false
    },
    [playList, setTrackId, trackId, paused, shuffle, repeat, content],
  )

  // used to identify the hook to the listeners
  // It also leads to each hook being able to subscribe to each action ONCE
  const uniqueId = useId()
  const currentCPMark = useRef<false | number>(false)

  // update UI directly to avoid excessive rendering
  const changeViews = useCallback(
    ({
      currentTime,
      instant = false,
    }: {
      currentTime: number
      instant?: boolean
    }) => {
      const progress = timeToProgress(currentTime, duration)
      const progressRef = document.getElementById("ProgressRef")
      const timestampRefLeft = document.getElementById("TimestampRefLeft")
      const timestampRefRight = document.getElementById("TimestampRefRight")
      if (!(progressRef && timestampRefLeft && timestampRefRight)) return
      if (instant || currentTime < 3) {
        progressRef.style.transition = "width 0s"
      }
      // update the hidden label to calculate the offsets
      progressRef.style.width = `${(progress / 100) * videoUIWidth}px`
      // update timestamps
      timestampRefLeft.textContent = secondsToTime(currentTime)
      timestampRefRight.textContent = secondsToTime(
        currentTime - Math.floor(duration),
      )
      // revert "instant" param
      setTimeout(() => {
        progressRef.style.transition = "width 0.2s linear"
      }, 100)

      // show/hide cue labels
      const label = document.getElementById("cuepointLabel")
      const labelText = document.getElementById("cuepointLabelText")
      const labelBox = document.getElementById("cuepointLabelBox")
      const labelTextReference = document.getElementById(
        "cuepointLabelTextReference",
      )
      const labelBoxReference = document.getElementById(
        "cuepointLabelBoxReference",
      )

      let found = false
      if (
        !(
          label &&
          labelText &&
          labelBox &&
          labelBoxReference &&
          labelTextReference
        )
      )
        return
      for (const key in cuePoints) {
        if (
          cuePoints[key].mark - cuePointTimespan / 2 <= currentTime &&
          cuePoints[key].mark + cuePointTimespan / 2 >= currentTime
        ) {
          currentCPMark.current = cuePoints[key].mark
          labelTextReference.innerText = cuePoints[key].title
          labelText.innerText = cuePoints[key].title
          labelBox.style.transform = `translate(0, 0)`
          label.style.opacity = "0"
          label.style.left = `${
            Math.round((cuePoints[key].mark / duration) * videoUIWidth) - s(151) // 151 ~ half of label width
          }px`
          const { x: xBox, left, width } = labelBox.getBoundingClientRect()
          const x = xBox || left
          if (x < 0) {
            labelBox.style.transform = `translate(${-Math.ceil(x)}px, 0)`
          } else if (x + width > dimensions.width) {
            labelBox.style.transform = `translate(${-Math.ceil(
              x + width - dimensions.width,
            )}px, 0)`
          }
          found = true
          label.style.opacity = "1"
          break
        }
      }
      if (!found) {
        currentCPMark.current = false
        label.style.opacity = "0"
      }
    },
    [duration],
  )

  const seekInstant = useCallback(
    (currentTime: number) => {
      seekStore(currentTime)
    },
    [seekStore],
  )
  const seekDebounced = useCallback(
    _.debounce((currentTime: number) => {
      seekStore(currentTime)
      setTimeout(() => play(), 100)
    }, 1000),
    [seekStore, play],
  )
  const seek = useCallback(
    ({
      step,
      relative = true,
      instant = false,
    }: {
      step: number
      relative?: boolean
      instant?: boolean
    }) => {
      const time = relative ? currentTimeRef.val + step : step
      const currentTime = Math.floor(
        time > duration ? duration : time < 0 ? 0 : time,
      )
      currentTimeRef.set(currentTime)
      if (instant) seekInstant(currentTime)
      else {
        pause()
        seekDebounced(currentTime)
      }
      changeViews({ currentTime, instant })
    },
    [changeViews, seekDebounced, seekInstant, duration, pause],
  )
  const changeChapter = useCallback(
    (direction: "left" | "right") => {
      let nextTimestamp
      if (!videoRef) return
      // if there is no playlist seek 30s
      if (!playList) {
        seek({ step: direction === "right" ? 30 : -30, instant: true })
        return
      }
      for (const key in cuePoints) {
        if (
          (direction === "right" &&
            cuePoints[key].mark > videoRef.currentTime + 5) ||
          (direction === "left" &&
            cuePoints[key].mark < videoRef.currentTime - 5)
        ) {
          nextTimestamp = cuePoints[key].mark
          if (direction === "right") break
        }
      }
      if (direction === "left" && !nextTimestamp) {
        if (currentTimeRef.val >= 2) nextTimestamp = 0
      }
      if (nextTimestamp !== undefined) {
        seek({ step: nextTimestamp, instant: true, relative: false })
        currentTimeRef.set(nextTimestamp)
        return
      }
      const nextUpResponse = nextUp(direction)
      // if we're skipping to next chapter, set currenttimeref to 0
      // + special case: if current time is less than 3 seconds and there are no chapters prior, seek to 0
      if (
        (direction === "right" && nextUpResponse) ||
        (direction === "left" && !nextUpResponse)
      ) {
        seek({ step: 0, instant: true, relative: false })
        currentTimeRef.set(0)
      }
    },
    [nextUp, videoRef, cuePoints, seek],
  )

  useEffect(() => {
    if (props.addListeners)
      setEventListener(
        PlayerEvent.Progress,
        () => {
          if (!videoRef) return
          const currentTime = videoRef.currentTime
          if (currentTimeRef.val > currentTime) return
          changeViews({ currentTime })
          currentTimeRef.set(currentTime)
        },
        uniqueId,
      )
    return () => {
      if (props.addListeners)
        removeEventListener(PlayerEvent.Progress, uniqueId)
    }
  }, [videoRef, duration])

  return { seek, changeChapter }
}
